import { Box, Typography, TextField, Button, Collapse } from "@mui/material";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReplyIcon from "@mui/icons-material/Reply";
import SendIcon from "@mui/icons-material/Send";
import useWindowDimensions from "../../helpers/window";
import useMessagesAPIClient from "../../api/messages/hooks/useMessagesAPIClient";

const daysAgo = (date) => {
  const now = new Date();
  const then = new Date(date);

  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  return `${diffDays} days ago`;
};

const MessageView = ({ activeMessageId, goBack }) => {
  const messages = useSelector((state) => state.messages);
  const { width } = useWindowDimensions();
  const isMobile = width <= 768;
  const [showReply, setShowReply] = useState(false);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const [replySent, setReplySent] = useState(false);
  const { sendReply } = useMessagesAPIClient();

  const MAX_MESSAGE_LENGTH = 2000;

  const activeMessage = useMemo(() => {
    return messages.find((msg) => msg.messageId === activeMessageId);
  }, [activeMessageId, messages]);

  // Check if this is a direct message (not global) - user can reply
  const canReply = activeMessage && activeMessage.sentTo !== "global";

  const handleSendReply = async () => {
    if (!replyText.trim() || sending) return;

    setSending(true);
    try {
      await sendReply(replyText.trim(), activeMessageId);
      setReplyText("");
      setShowReply(false);
      setReplySent(true);
    } catch (error) {
      console.error("Failed to send reply:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Box
      className="messages_view scroller"
      sx={{ display: "flex", flexDirection: "column", height: "100%" }}
    >
      {isMobile && (
        <ArrowBackIcon className="overlay_back-icon" onClick={goBack} />
      )}

      {activeMessage ? (
        <>
          {activeMessage.sentAt !== 1 && (
            <Typography
              sx={{
                borderBottom: "1px solid #004a3c",
                padding: "8px",
                color: "#004a3c",
                textAlign: "right",
              }}
            >
              {daysAgo(activeMessage.sentAt)}
            </Typography>
          )}
          <Box sx={{ flex: 1 }}>
            <Typography variant="h2">{activeMessage.subject}</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {activeMessage.message}
            </Typography>
          </Box>

          {/* Reply section for direct messages */}
          {canReply && (
            <Box sx={{ mt: 2, borderTop: "1px solid #e0e0e0", pt: 2 }}>
              {replySent ? (
                <Box
                  sx={{
                    bgcolor: "#e8f5e9",
                    p: 2,
                    borderRadius: 2,
                    textAlign: "center",
                  }}
                >
                  <Typography
                    variant="body2"
                    color="success.main"
                    sx={{ mb: "0 !important" }}
                  >
                    ✓ Your reply has been sent!
                  </Typography>
                </Box>
              ) : (
                <>
                  {!showReply ? (
                    <Button
                      variant="outlined"
                      startIcon={<ReplyIcon />}
                      onClick={() => setShowReply(true)}
                      sx={{
                        borderColor: "#197b68",
                        color: "#197b68",
                        "&:hover": {
                          borderColor: "#004a3c",
                          bgcolor: "#e8f5e9",
                        },
                      }}
                    >
                      Reply to this message
                    </Button>
                  ) : (
                    <Collapse in={showReply}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        <TextField
                          multiline
                          rows={3}
                          placeholder="Type your reply..."
                          value={replyText}
                          onChange={(e) => {
                            if (e.target.value.length <= MAX_MESSAGE_LENGTH) {
                              setReplyText(e.target.value);
                            }
                          }}
                          fullWidth
                          disabled={sending}
                          inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                          helperText={`${replyText.length}/${MAX_MESSAGE_LENGTH}`}
                        />
                        <Box
                          sx={{
                            display: "flex",
                            gap: 1,
                            justifyContent: "flex-end",
                          }}
                        >
                          <Button
                            variant="text"
                            onClick={() => {
                              setShowReply(false);
                              setReplyText("");
                            }}
                            disabled={sending}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="contained"
                            endIcon={<SendIcon />}
                            onClick={handleSendReply}
                            disabled={!replyText.trim() || sending}
                            sx={{
                              bgcolor: "#197b68",
                              "&:hover": { bgcolor: "#004a3c" },
                            }}
                          >
                            {sending ? "Sending..." : "Send Reply"}
                          </Button>
                        </Box>
                      </Box>
                    </Collapse>
                  )}
                </>
              )}
            </Box>
          )}
        </>
      ) : (
        <Box
          sx={{
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography variant="h5" sx={{ mb: 0 }}>
            Select a message to view
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MessageView;
